import React from "react";
import SectionHeading from "./SectionHeading";

const QUOTES = [
  {
    stat: "847 / 340 / 1,200+",
    statLabel: "high-value guests · influencers identified · playbooks triggered",
    quote:
      "Detected 847 high-value guests, 340 influencers identified, triggered 1,200+ personalized playbooks.",
    by: "Luxury Hotel Group · Singapore",
    logo: null,
  },
  {
    stat: "18% → 42%",
    statLabel: "repeat-visit rate in 6 months",
    quote: "Went from 18% repeat visits to 42% in 6 months.",
    by: "Premium Restaurant · Miami",
    logo: null,
  },
  {
    stat: "3x",
    statLabel: "social reach growth",
    quote:
      "Social reach tripled. Product team now uses interaction data for development.",
    by: "Retail Flagship · Tokyo",
    logo: null,
  },
];

/**
 * Industry-focused bento grid — 9 tiles, each with a representative stock photo
 * and the industry label. Responsive: stacks on mobile, asymmetric grid at md+.
 */
const INDUSTRIES = [
  {
    label: "Luxury Hotels",
    img: "https://images.unsplash.com/photo-1566073771259-6a8506099945?auto=format&fit=crop&w=1200&q=80",
    className: "md:col-span-4 md:row-span-2",
  },
  {
    label: "Bars & Restaurants",
    img: "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1400&q=80",
    className: "md:col-span-5 md:row-span-1",
  },
  {
    label: "Retail Flagships",
    img: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&w=900&q=80",
    className: "md:col-span-3 md:row-span-2",
  },
  {
    label: "Beauty & Wellness",
    img: "https://images.unsplash.com/photo-1540555700478-4be289fbecef?auto=format&fit=crop&w=1400&q=80",
    className: "md:col-span-5 md:row-span-2",
  },
  {
    label: "Museums & Culture",
    img: "https://images.unsplash.com/photo-1554907984-15263bfd63bd?auto=format&fit=crop&w=1200&q=80",
    className: "md:col-span-4 md:row-span-1",
  },
  {
    label: "Corporate Events",
    img: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=900&q=80",
    className: "md:col-span-3 md:row-span-1",
  },
  {
    label: "Nightlife",
    img: "https://images.unsplash.com/photo-1566417713940-fe7c737a9ef2?auto=format&fit=crop&w=1200&q=80",
    className: "md:col-span-4 md:row-span-1",
  },
  {
    label: "Tourism & Travel",
    img: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1200&q=80",
    className: "md:col-span-4 md:row-span-1",
  },
  {
    label: "Experiential Activations",
    img: "https://images.unsplash.com/photo-1492684223066-81342ee5ff30?auto=format&fit=crop&w=1200&q=80",
    className: "md:col-span-4 md:row-span-1",
  },
];

export default function SocialProof() {
  return (
    <section
      data-testid="social-proof"
      className="relative py-24 lg:py-32 border-b border-stori-border bg-stori-bg"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading
          eyebrow="WHY BRANDS CHOOSE STORI"
          title="Built for teams that take guest intelligence seriously."
          lead="Stori powers guest experiences for operators across nine industries — from Michelin restaurants to flagship retail."
        />

        {/* Industry bento grid */}
        <div
          data-testid="industry-grid"
          className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-3 lg:gap-4 md:auto-rows-[240px] lg:auto-rows-[260px]"
        >
          {INDUSTRIES.map((item, i) => (
            <figure
              key={item.label}
              data-testid={`industry-tile-${i}`}
              className={`relative group overflow-hidden rounded-xl border border-stori-border bg-stori-surface1 min-h-[260px] md:min-h-0 ${item.className}`}
            >
              <img
                src={item.img}
                alt={item.label}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              />
              {/* Bottom-left label overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-stori-bg via-stori-bg/10 to-transparent pointer-events-none" />
              <figcaption className="absolute left-5 bottom-4 lg:left-6 lg:bottom-5">
                <span className="font-heading text-xl lg:text-2xl font-semibold text-white tracking-tight drop-shadow">
                  {item.label}
                </span>
              </figcaption>
              {/* Hover cyan accent corner */}
              <span className="absolute top-4 right-4 w-2 h-2 rounded-full bg-stori-cyan opacity-0 group-hover:opacity-100 transition-opacity" />
            </figure>
          ))}
        </div>

        {/* Quote row */}
        <div className="mt-20 grid md:grid-cols-3 gap-4">
          {QUOTES.map((q, i) => (
            <div
              key={i}
              data-testid={`quote-card-${i}`}
              className="p-7 lg:p-8 rounded-xl border border-stori-border bg-stori-surface1 hover:border-stori-cyan/40 transition-colors flex flex-col"
            >
              <div className="font-heading text-5xl lg:text-6xl xl:text-7xl font-bold text-stori-cyan cyan-text-glow leading-[1.05] tracking-tight">
                {q.stat}
              </div>
              <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-stori-muted">
                {q.statLabel}
              </div>
              <blockquote className="mt-7 text-white text-lg leading-snug">
                &ldquo;{q.quote}&rdquo;
              </blockquote>
              <div className="mt-auto pt-6 border-t border-stori-border">
                <div className="text-sm text-stori-muted">— {q.by}</div>
                {/* Logo slot — placeholder until client supplies real marks */}
                <div
                  data-testid={`quote-logo-${i}`}
                  className="mt-4 h-10 flex items-center"
                >
                  {q.logo ? (
                    <img
                      src={q.logo}
                      alt={`${q.by} logo`}
                      className="h-7 w-auto object-contain opacity-80"
                    />
                  ) : (
                    <div className="h-8 w-28 rounded border border-dashed border-stori-border flex items-center justify-center">
                      <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-stori-muted/60">
                        client logo
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
